// Generic links let an author link to a step without committing to a cohort by
// standing a "_" in for the cohort ID. The placeholder is resolved against the
// cohort the reader is currently viewing.
// See https://help.pathwright.com/en/articles/9101692

export const GENERIC_COHORT_PLACEHOLDER = "_"

// NOTE: using RegExp constructor to avoid Babel transpile bug due to named groups.
export const COHORT_SECTION_RE = new RegExp(
  "\\/(?<library>library)\\/(?<resourceSlug>[a-zA-Z0-9_-]*-(?<resourceId>\\d+))\\/(?<cohortId>(\\d+|_))\\/(?<section>[a-z]+)\\/"
)

export type GenericLinkContext = {
  cohortId?: string | number | null
  resourceId?: string | number | null
}

/** Read the cohort context out of a URL, defaulting to the current location. */
export function getGenericLinkContext(url?: string): GenericLinkContext {
  const target =
    url ?? (typeof window !== "undefined" ? window.location.pathname : "")
  const groups = target.match(COHORT_SECTION_RE)?.groups

  return {
    cohortId: groups?.cohortId,
    resourceId: groups?.resourceId
  }
}

/**
 * Resolve a generic link's "_" cohort placeholder to the cohort in `context`.
 * Returns the url untouched unless it's a generic link into the same resource,
 * since a cohort ID is only meaningful within the resource that owns it.
 */
export function resolveGenericLinkUrl(
  url: string,
  context: GenericLinkContext
): string {
  // Callers include untyped legacy JS, so don't assume a string.
  if (typeof url !== "string" || !url.includes(GENERIC_COHORT_PLACEHOLDER))
    return url

  const match = url.match(COHORT_SECTION_RE)
  const groups = match?.groups
  if (!groups || groups.cohortId !== GENERIC_COHORT_PLACEHOLDER) return url

  const { cohortId, resourceId } = context
  if (!cohortId || cohortId === GENERIC_COHORT_PLACEHOLDER) return url
  // Context IDs come through as numbers from the app and strings from a url.
  if (!resourceId || String(resourceId) !== groups.resourceId) return url

  const cohortSectionUrl = `/${groups.library}/${groups.resourceSlug}/${cohortId}/${groups.section}/`
  return url.replace(match![0], cohortSectionUrl)
}

const HREF_RE = /(<a\b[^>]*?\shref=)(["'])(.*?)\2/gi

/** Resolve the generic links in a chunk of authored HTML. */
export function resolveGenericLinksInHtml(
  html: string,
  context: GenericLinkContext
): string {
  if (typeof html !== "string" || !html.includes(GENERIC_COHORT_PLACEHOLDER))
    return html

  return html.replace(
    HREF_RE,
    (_match, prefix: string, quote: string, href: string) =>
      `${prefix}${quote}${resolveGenericLinkUrl(href, context)}${quote}`
  )
}
